import { type FC, memo, useMemo } from "react";
import { Box } from "@/components/essentials/box";
import { Typography } from "@/components/essentials/typography";
import { formatPrice } from "@/modules/utils/price";
import { getPriceType } from "./module";
import {
  foreverCouponTypoTestId,
  normalTypoTestId,
  onceCouponTypo1TestId,
  onceCouponTypo2TestId,
  repeatingCouponTypo1TestId,
  repeatingCouponTypo2TestId,
} from "./const";
import { type PlanData } from "@/components/fragments/argyle/plan-data-table";

export interface PriceProps {
  readonly data: PlanData;
  readonly promotionCode?: string;
}

export const Price: FC<PriceProps> = memo(({ data, promotionCode }) => {
  const { price, subscriptionCoupon } = data;
  const priceType = getPriceType(data);
  const discountedPrice = price - (subscriptionCoupon?.discountAmount ?? 0);

  const is6mPlan: boolean = useMemo(() => {
    return (
      // MKT_6点60日交換半年支払い
      data.id === "89749bb4-c2bb-4b68-9132-eea48fa1b2b5" ||
      // MKT_スタンダードプラン(6点60日交換半年払い)
      data.id === "d0f8da6e-08bd-4ba8-a5ca-1096603557dc"
    );
  }, [data.id]);

  const is1yPlan: boolean = useMemo(() => {
    // MKT_スタンダードプラン(6点60日交換一年払い)
    return data.id === "b6132fad-608e-4e3e-bc59-6389eec35a29";
  }, [data.id]);

  const firstPricePrefix: string = useMemo(() => {
    if (is6mPlan) {
      return "半年間";
    } else if (is1yPlan) {
      return "一年間";
    } else {
      return "初月";
    }
  }, [is1yPlan, is6mPlan]);

  const normalPricePrefix: string = useMemo(() => {
    if (is6mPlan) {
      return "半年後以降";
    } else if (is1yPlan) {
      return "一年後以降";
    } else {
      return "2ヶ月目以降";
    }
  }, [is1yPlan, is6mPlan]);

  switch (priceType) {
    case "normal":
      return (
        <Typography variant="text2" data-testid={normalTypoTestId}>
          {formatPrice(price)}
        </Typography>
      );
    case "once":
      // TODO: 3ヶ月連続割引を実装したい
      if (promotionCode === "BABYPA2410A") {
        return (
          <Box>
            <Typography variant="text2-bold" color="secondary.main" data-testid={repeatingCouponTypo1TestId}>
              初月：{formatPrice(0)}
              <br />
              2ヶ月目：{formatPrice(1990)}
              <br />
              3ヶ月目：{formatPrice(2786)}
            </Typography>
            <Typography variant="text2" data-testid={repeatingCouponTypo2TestId}>
              4ヶ月目以降：{formatPrice(3980)}
            </Typography>
          </Box>
        );
      } else {
        return (
          <Box>
            <Typography variant="text2-bold" color="secondary.main" data-testid={onceCouponTypo1TestId}>
              {firstPricePrefix}：{formatPrice(discountedPrice <= 0 ? 0 : discountedPrice)}
            </Typography>
            <Typography variant="text2" data-testid={onceCouponTypo2TestId}>
              {normalPricePrefix}：{formatPrice(price)}
            </Typography>
          </Box>
        );
      }
    case "repeating":
      return (
        <Box>
          <Typography variant="text2-bold" color="secondary.main" data-testid={repeatingCouponTypo1TestId}>
            初月-{subscriptionCoupon?.durationInMonths ?? 0}ヶ月目：
            {formatPrice(discountedPrice <= 0 ? 0 : discountedPrice)}
          </Typography>
          <Typography variant="text2" data-testid={repeatingCouponTypo2TestId}>
            {(subscriptionCoupon?.durationInMonths ?? 0) + 1}ヶ月目以降：{formatPrice(price)}
          </Typography>
        </Box>
      );
    case "forever":
      return (
        <Typography variant="text2-bold" color="secondary.main" data-testid={foreverCouponTypoTestId}>
          {formatPrice(discountedPrice <= 0 ? 0 : discountedPrice)}
        </Typography>
      );
  }
});

Price.displayName = "Price";
